<script setup>
  import { computed, onMounted } from 'vue'
  import { useMapUIStore } from "@/stores/MapUIStore";
  import { useProductStore } from "@/stores/ProductStore";
  import { useUserStore } from "@/stores/UserStore";
  import { useMapStore } from '@/stores/MapStore';
  import { useStormStore } from "@/stores/StormStore";
  import ControlButton from '@/components/controls/ControlButton.vue';
  import Icon from '@/components/icons/Icon.vue';
  import useMapConfigurationGlobals from '@/composables/useMapConfigurationGlobals';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';

  const { $STORM_TRACK } = useSettingsGlobals();
  const { $DRAWING_TOOLS, $MAP_FAVORITING, } = useMapConfigurationGlobals();  

  const mapUIStore = useMapUIStore();
  const productStore = useProductStore();
  const userStore = useUserStore();
  const mapStore = useMapStore();
  const stormStore = useStormStore();

  const spaghettiPlotCount = computed(() => stormStore.spaghettiPlots.length);
  const conePlotCount = computed(() => stormStore.conePlots.length);
  const allSpaghettiPlotsSelected = computed(() => stormStore.visibleSpaghettiPlots.length === stormStore.spaghettiPlots.length);
  const allConePlotsSelected = computed(() => stormStore.visibleConePlots.length === stormStore.conePlots.length);

  // TODO: Composable
  function emitEvent(event, detail = {}) {
    window.$EventBus.dispatchEvent(event, detail);
  }  

  function controlsClickHandler(e) {
    const clickedButton = e.target.closest('[data-action]');
    if (!clickedButton) return;

    const action = clickedButton.dataset.action;

    switch(action) {
      case 'zoom-in':
        emitEvent('map-ui-zoom-in');
        break;
      case 'zoom-out':
        emitEvent('map-ui-zoom-out');
        break;
      case 'storm-plot-cone':
        stormStore.setPlotType('cone');
        break;
      case 'storm-plot-spaghetti':
        stormStore.setPlotType('spaghetti');
        break;
      case 'save-map':
        handleSaveMap();
        break;
      case 'drawing-tools':
        handleDrawingTools();
        break;
      case 'settings':
        userStore.showSettingsModal = !userStore.showSettingsModal;
        break;
    }
  }

  function handleDrawingTools() {
    mapUIStore.toggleDrawingTools();
  }

  async function handleSaveMap() {
    if (!userStore.currentProfileId) await userStore.getProfiles(true);

    emitEvent('map-ui-save-map', { profileId: userStore.currentProfileId });
  }

  async function handleMapSavedToProfile(e) {
    const { containerName, containerId } = e.detail;

    mapStore.state.name = containerName;
    mapStore.state.shortName = containerName;
    mapStore.state.id = containerId;

    await userStore.getProfiles(true);

    if (jwc) {
      jwc.toaster.toast({
        type: 'favorite',
        content: `<strong>${containerName}</strong> saved to profile <strong>${userStore.currentProfile.name}</strong>`,
      })
    }  
  }

  onMounted(() => window.$EventBus.addEventListener('cosmos-map-saved', handleMapSavedToProfile))
</script>

<template>

  <div @click.prevent="controlsClickHandler" class="map__controls jwx-map__controls--primary">

    <div v-if="$MAP_FAVORITING && !productStore.hasFeatureUpgrade(37)" class="jwx-map-block jwx-map-block--transparent-border">
      <ul class="jwx-map-block__content jwx-map-block__items">
        <li class="jwx-map-block__item">
          <ControlButton
            data-action="save-map"
          >
            <Icon v-if="mapStore.state.id > 999 && mapStore.state.name !== 'Unsaved Map'" name="star-solid" class="jwx-button-control__icon" />  
            <Icon v-else name="star" class="jwx-button-control__icon" :data-attention="mapStore.state.id === 'unsaved'" />  
          </ControlButton>
        </li>
      </ul>
    </div>

    <div v-if="$STORM_TRACK" class="jwx-map-block jwx-map-block--transparent-border">
      <ul class="jwx-map-block__content jwx-map-block__items">
        <li class="jwx-map-block__item" style="z-index: 1;">
          <ControlButton
            :disabled="spaghettiPlotCount === 0"
            class="jwx-button-control--flow"
            :class="{ 'selected': stormStore.plotType === 'spaghetti' }"
            data-action="storm-plot-spaghetti"
          >
            <Icon name="spaghetti-plot" class="jwx-button-control__icon" /> 
            <span
              v-if="spaghettiPlotCount > 0 && allSpaghettiPlotsSelected"
              class="jwx-button-control__chip"
            >{{ spaghettiPlotCount }}</span>
            <span
              v-else-if="spaghettiPlotCount > 0"
              class="jwx-button-control__chip"
            >{{ stormStore.visibleSpaghettiPlots.length }} / {{ spaghettiPlotCount }}</span>
          </ControlButton>
        </li>        
        <li class="jwx-map-block__item" style="z-index: 0;">
          <ControlButton
            :disabled="conePlotCount === 0"
            class="jwx-button-control--flow"
            :class="{ 'selected': stormStore.plotType === 'cone' }"
            data-action="storm-plot-cone"
          >
            <Icon name="cone-plot" class="jwx-button-control__icon" /> 
            <span
              v-if="conePlotCount > 0 && allConePlotsSelected"
              class="jwx-button-control__chip"
            >{{ conePlotCount }}</span>
            <span
              v-else-if="conePlotCount > 0"
              class="jwx-button-control__chip"
            >{{ stormStore.visibleConePlots.length }} / {{ conePlotCount }}</span>
          </ControlButton>
        </li>       
      </ul>
    </div>

    <div 
      v-if="$DRAWING_TOOLS" 
      class="jwx-map-block jwx-map-block--transparent-border" 
    >
      <ul class="jwx-map-block__content jwx-map-block__items">
        <li class="jwx-map-block__item">
          <ControlButton
            :class="{ 'selected': mapUIStore.showDrawingTools }"
            data-action="drawing-tools"
          >
            <Icon name="drawing-tools" class="jwx-button-control__icon" />  
          </ControlButton>
        </li>
      </ul>
    </div>    

    <div v-if="mapUIStore.showZoomControl" class="jwx-map-block jwx-map-block--transparent-border">
      <ul class="jwx-map-block__content jwx-map-block__items">
        <li class="jwx-map-block__item">
          <ControlButton data-action="zoom-out">
            <Icon name="minus" class="jwx-button-control__icon" /> 
          </ControlButton>
        </li>        
        <li class="jwx-map-block__item">
          <ControlButton data-action="zoom-in">
            <Icon name="plus" class="jwx-button-control__icon" /> 
          </ControlButton>
        </li>       
      </ul>
    </div>

    <!-- <div class="jwx-map-block jwx-map-block--transparent-border">
      <ul class="jwx-map-block__content jwx-map-block__items">
        <li class="jwx-map-block__item">
          <ControlButton data-action="settings">
            <Icon name="gear" class="jwx-button-control__icon" /> 
          </ControlButton>
        </li>
      </ul>
    </div> -->

  </div>

</template>